import {
  MATCHING_FIELDS_MOUNTS,
  MEMBER_OPTIONAL_MATCHING_FIELDS,
  MEMBER_OPTIONAL_MATCHING_SKIP_NOTICE,
  isRegistrationMount,
  visibleFieldsFor,
  type MatchingFieldsMountPoint,
  type MemberOptionalMatchingField,
} from '@h2hspark/contracts';
import * as React from 'react';

/**
 * The four optional matching fields, rendered (`MEM-045` AC-3).
 *
 * **One implementation, four mount points, no branch on which one.** `ORG-013`'s
 * `ProfileEditorPanel` is the pattern and its header carries the rule: *"Keeping the difference in
 * a table rather than in branches inside the component is what stops the two drifting into two
 * editors."* The table is `MATCHING_FIELDS_MOUNTS` in `@h2hspark/contracts`; this file reads a row
 * out of it and renders it, and there is no `mountPoint === …` comparison anywhere below except
 * the one that decides whether to draw a Save button — which is `saveAction` being `null`, i.e.
 * the table again.
 *
 * Three of the mounts are registration steps and the fourth is the preferences panel. That the
 * wizard is three mounts rather than one is the client's own placement: each field sits beside the
 * question it belongs with rather than in a block of leftovers at the end.
 *
 * ## Why an unpopulated vocabulary hides the control
 *
 * `MEM-004`'s not-ready state exists because a member cannot finish a step whose only question has
 * no answers. These questions are optional, so an unauthored `industry` vocabulary is not a step
 * that cannot be answered — it is a question that is not being asked. Rendering an apology there
 * would stop a wizard over nothing; rendering an empty `<select>` would be the defect `MEM-004`
 * AC-3 names. So: no terms, no control (`MEM-045` AC-2).
 */

export interface MatchingFieldValues {
  readonly [attribute: string]: string | undefined;
}

export interface VocabularyTerm {
  readonly termId: string;
  readonly displayName: string;
  readonly description?: string | undefined;
}

export interface MatchingFieldsPanelProps {
  readonly mountPoint: MatchingFieldsMountPoint;
  readonly values: MatchingFieldValues;
  /** Keyed by vocabulary name. A vocabulary with no terms hides its field. */
  readonly terms: Record<string, ReadonlyArray<VocabularyTerm>>;
  /** `''` means the member cleared it. The caller decides whether that is an erase on the wire. */
  readonly onChange: (attribute: string, value: string) => void;
  readonly disabled?: boolean;
  /**
   * Attributes another control on the same screen already owns — see
   * `MatchingPreferencesForm`'s `omitAttributes`, which is the only caller that passes any.
   *
   * Not a branch on the mount: the mount table still says which fields belong to this mount, and
   * this says which of them somebody else is already drawing.
   */
  readonly omitAttributes?: ReadonlyArray<string>;
}

const SELECT_SHELL =
  'h-[52px] w-full rounded-md border-[1.5px] border-border bg-card px-[12px] text-md text-text-body outline-none focus:border-primary disabled:opacity-60';

const TEXT_SHELL =
  'h-[52px] w-full rounded-md border-[1.5px] border-border bg-card px-[14px] text-md text-text-body outline-none placeholder:text-text-subtle focus:border-primary disabled:opacity-60';

export function MatchingFieldsPanel({
  mountPoint,
  values,
  terms,
  onChange,
  disabled,
  omitAttributes = [],
}: MatchingFieldsPanelProps): React.ReactElement | null {
  const mount = MATCHING_FIELDS_MOUNTS[mountPoint];
  const fields = visibleFieldsFor(mountPoint, terms).filter(
    (field) => !omitAttributes.includes(field.attribute),
  );
  if (fields.length === 0) return null;

  return (
    <div className={isRegistrationMount(mountPoint) ? 'mt-[14px] space-y-[14px]' : 'space-y-[14px]'}>
      {/* The heading, the blurb and the skip notice render on the `preferences` mount alone — the
          registration mounts carry `null` for all three, because the step's own question already
          asks. So these three sizes are the 10 September prototype's "Improve your matches"
          block (`Web App.dc.html` lines 870–892) and touch no wizard step. */}
      {mount.heading !== null ? (
        <h2 className="text-xl tracking-tight md:text-2xl">{mount.heading}</h2>
      ) : null}
      {mount.blurb !== null ? (
        <p className="max-w-[700px] text-sm leading-relaxed text-text-body md:text-md">
          {mount.blurb}
        </p>
      ) : null}
      {fields.map((field) => (
        <MatchingField
          key={field.id}
          field={field}
          value={values[field.attribute] ?? ''}
          terms={field.vocabulary === null ? [] : (terms[field.vocabulary] ?? [])}
          onChange={(next) => onChange(field.attribute, next)}
          disabled={disabled === true}
        />
      ))}
      {mount.heading !== null ? (
        <p className="text-xs text-text-subtle">{MEMBER_OPTIONAL_MATCHING_SKIP_NOTICE}</p>
      ) : null}
    </div>
  );
}

/**
 * One field.
 *
 * The `· optional` marker is on every one of them and is not decoration: it is the only thing on
 * the control that says a member may move past it, and `MEM-045` AC-1 is that they can.
 */
function MatchingField({
  field,
  value,
  terms,
  onChange,
  disabled,
}: {
  readonly field: MemberOptionalMatchingField;
  readonly value: string;
  readonly terms: ReadonlyArray<VocabularyTerm>;
  readonly onChange: (value: string) => void;
  readonly disabled: boolean;
}): React.ReactElement {
  const id = `matching-${field.id}`;
  return (
    <label className="block min-w-0" htmlFor={id}>
      <span className="mb-[7px] block font-brand text-sm font-semibold text-foreground">
        {field.label}
        <span className="font-medium text-text-subtle"> · optional</span>
      </span>
      <span className="mb-2 block text-xs text-muted-foreground">{field.hint}</span>
      {field.control === 'free-text' ? (
        <input
          id={id}
          name={field.attribute}
          type="text"
          className={TEXT_SHELL}
          maxLength={field.maxLength ?? undefined}
          placeholder={field.placeholder}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        />
      ) : (
        <select
          id={id}
          name={field.attribute}
          className={SELECT_SHELL}
          value={value}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
        >
          {/* The empty option is how a member un-answers. Without it, choosing once would be
              permanent — which would make the field optional only until it was touched. */}
          <option value="">{field.placeholder}</option>
          {terms.map((term) => (
            <option key={term.termId} value={term.termId}>
              {term.displayName}
            </option>
          ))}
        </select>
      )}
      {field.control === 'free-text' && field.maxLength !== null ? (
        <span className="mt-[6px] block text-right text-xs text-text-subtle">
          {value.length}/{field.maxLength}
        </span>
      ) : null}
    </label>
  );
}

/** Every attribute the panel can write, for a caller assembling a patch. Derived, so a fifth
 * field is one entry in the contracts table and nothing here. */
export const MATCHING_FIELD_ATTRIBUTES = MEMBER_OPTIONAL_MATCHING_FIELDS.map(
  (field) => field.attribute,
);
